import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Factory } from "lucide-react";
import { toast } from "sonner";

// The page the "Reset your password" e-mail links to (POST /auth/reset-password).
// The link works once, for 30 minutes.
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [f, setF] = useState({ next: "", again: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (f.next.length < 6) return setError("Use at least 6 characters.");
    if (f.next !== f.again) return setError("The passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new: f.next });
      toast.success("Password changed — sign in with your new password");
      nav("/login", { replace: true });
    } catch (err) { setError(formatApiError(err?.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-white">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center"><Factory className="w-5 h-5 text-white" /></div>
          <div className="text-lg font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>Canares Automation</div>
        </div>
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Choose a new password</h1>
        {!token ? (
          <p className="mt-4 text-sm text-red-600">This link is incomplete. Ask for a new one from the login page.</p>
        ) : (
          <form onSubmit={save} className="mt-6 space-y-4" data-testid="reset-password-form">
            <div>
              <Label htmlFor="rp-new">New password</Label>
              <PasswordInput id="rp-new" autoComplete="new-password" value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} required className="mt-1.5" data-testid="reset-password-new" />
            </div>
            <div>
              <Label htmlFor="rp-again">Repeat it</Label>
              <PasswordInput id="rp-again" autoComplete="new-password" value={f.again} onChange={(e) => setF({ ...f, again: e.target.value })} required className="mt-1.5" data-testid="reset-password-again" />
            </div>
            {error && <div className="text-sm text-red-600" data-testid="reset-password-error">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="reset-password-save">Save new password</Button>
          </form>
        )}
        <div className="mt-6 text-sm"><Link to="/login" className="text-blue-700 hover:text-blue-800 font-medium">Back to sign in</Link></div>
      </div>
    </div>
  );
}
